import { isCollectionPass, normalizeIdrCode } from '@/utils/idr-code';
import { redeemMessageForCode } from '@/utils/redeem-messages';

/**
 * Next Steps DEV-ONLY mock — a localStorage-backed state machine that lets a
 * PM click through every Next Steps access/unlock/payment/voucher flow on web
 * with NO backend, and see what each pastoral status experiences.
 *
 * Mirrors the two existing dev-only mock patterns in this codebase:
 *   - `mockLeaderAccess` override in store/modules/courses.js (checkLeaderAccess)
 *   - the /payment-demo harness (utils/payment-demo.js + pages/payment-demo)
 *
 * It is inert unless `isNsMockEnabled()` is true (never active in production —
 * callers additionally gate on `process.env.VUE_APP_NODE_ENV !== 'production'`),
 * so the real API-driven access path is untouched when the mock is off.
 *
 * State is pure JS (no Vue) so it can be imported from Vuex modules, route
 * guards, and the console page alike.
 */

import { seedDevFlags } from '@/composables/useFeatureFlags';

// ── Persistence ─────────────────────────────────────────────────────────────
export const NS_MOCK_KEY = 'nsMockState';

// Program slugs (see BACKGROUND FACTS — two Next Steps programmes).
export const PROGRAM_SLUGS = ['next-steps-p1', 'next-steps-p2'];

// Part 1 lesson slugs (seed fixtures) — L3 & L4 are the class-unlock TRIAL lessons.
const P1_LESSON_SLUGS = [
  'assurance-of-salvation',
  'relationship-with-god',
  'listening-to-god',
  'talking-to-god',
  'kinds-of-hearts',
];
const P1_TRIAL_LESSON_SLUGS = ['listening-to-god', 'talking-to-god'];

// Part 2 placeholder lesson slugs.
const P2_LESSON_SLUGS = ['part-2-lesson-1', 'part-2-lesson-2', 'part-2-lesson-3'];

// Pastoral ladder — sort values mirror GET /profile/pastoral-mapping (V=1, N=2,
// G=3, I=4, R=5, leader roles 11+). Used to drive the picker + the guard.
export const PASTORAL_OPTIONS = [
  { label: 'V — Visitor', sort: 1 },
  { label: 'N — New Friend', sort: 2 },
  { label: 'G — Guest', sort: 3 },
  { label: 'I — Established', sort: 4 },
  { label: 'R — Regular', sort: 5 },
  { label: 'Leader (CGL)', sort: 11 },
];

// Answer-key mode (`prefillBlanks`) is a Regular's: granted at sort 5 (R) and
// every leader role above it. Named so the one rule "R and above sees the answers"
// has a single home — the intro gate reads the same constant instead of the
// derived `prefillBlanks` flag, which is server-set and could be repurposed.
export const REGULAR_PASTORAL_SORT = 5;

const TIER_RANK = { PREVIEW: 0, TRIAL: 1, FULL: 2 };

// Mock CGs the member can set as their main CG (for the link-CG precondition).
export const MOCK_CGS = [
  { id: 'cg-1', name: 'CG Alpha' },
  { id: 'cg-2', name: 'CG Bravo' },
  { id: 'cg-3', name: 'CG Charlie' },
];

export function getMockCgs() {
  return MOCK_CGS;
}

function defaultProgramState() {
  return {
    tier: 'PREVIEW',
    unlockedLessonSlugs: [],
    source: null,
  };
}

function defaultState() {
  return {
    enabled: false,
    roleSort: 1,
    // Main-CG link is the one precondition for redeeming a book code (confirmed
    // model 2026-07-22 — no ACTS verification, no expiry).
    cgLinked: false,
    linkedCgId: null,
    programs: {
      [PROGRAM_SLUGS[0]]: defaultProgramState(),
      [PROGRAM_SLUGS[1]]: defaultProgramState(),
    },
    // Tracks which program a mock purchase is in flight for, so confirmPurchase
    // knows what to grant (the real API keys this off the transaction reference
    // server-side; the mock has no server, so it's stashed here instead).
    pendingPurchaseSlug: null,
    // Mock stand-in for IDR's burn ledger: { [canonicalCode]: programSlug }.
    // Real single-use enforcement lives at IDR; this only exists so the mock can
    // show "already used" and the replay-on-retry path without a partner API.
    burnedCodes: {},
  };
}

function readState() {
  if (typeof window === 'undefined') return defaultState();
  try {
    const raw = window.localStorage.getItem(NS_MOCK_KEY);
    if (!raw) return defaultState();
    const parsed = JSON.parse(raw) || {};
    const fallback = defaultState();
    return {
      ...fallback,
      ...parsed,
      burnedCodes: { ...(parsed.burnedCodes || {}) },
      programs: {
        [PROGRAM_SLUGS[0]]: {
          ...fallback.programs[PROGRAM_SLUGS[0]],
          ...(parsed.programs?.[PROGRAM_SLUGS[0]] || {}),
        },
        [PROGRAM_SLUGS[1]]: {
          ...fallback.programs[PROGRAM_SLUGS[1]],
          ...(parsed.programs?.[PROGRAM_SLUGS[1]] || {}),
        },
      },
    };
  } catch (e) {
    return defaultState();
  }
}

function writeState(nextState) {
  if (typeof window === 'undefined') return nextState;
  try {
    window.localStorage.setItem(NS_MOCK_KEY, JSON.stringify(nextState));
  } catch (e) {
    /* non-blocking */
  }
  return nextState;
}

// ── Public state accessors ──────────────────────────────────────────────────
export function isNsMockEnabled() {
  if (process.env.VUE_APP_MOCK_NEXT_STEPS === 'true') return true;
  return readState().enabled === true;
}

export function getNsMockState() {
  return readState();
}

export function setNsMockState(patch) {
  const current = readState();
  return writeState({ ...current, ...patch });
}

export function resetNsMock() {
  return writeState(defaultState());
}

export function setRoleSort(roleSortValue) {
  return setNsMockState({ roleSort: roleSortValue });
}

export function getMockRoleSort() {
  return readState().roleSort;
}

// ── Pending purchase tracking (mock stand-in for server-side reference→slug) ─
export function setPendingPurchaseSlug(slug) {
  return setNsMockState({ pendingPurchaseSlug: slug });
}

export function getPendingPurchaseSlug() {
  return readState().pendingPurchaseSlug || null;
}

export function clearPendingPurchaseSlug() {
  return setNsMockState({ pendingPurchaseSlug: null });
}

// ── State-machine transitions (mutate + persist; idempotent; never downgrade
// FULL — tiers only ever move PREVIEW → TRIAL → FULL) ───────────────────────
function maxTier(current, target) {
  return (TIER_RANK[current] ?? 0) >= (TIER_RANK[target] ?? 0) ? current : target;
}

// Class/QR unlock: grants TRIAL and unlocks the two trial lessons (P1 only has
// trial-lesson fixtures; calling this for P2 just raises the tier).
export function grantTrial(slug) {
  const state = readState();
  const program = state.programs[slug];
  if (!program) return state;
  program.tier = maxTier(program.tier, 'TRIAL');
  if (slug === PROGRAM_SLUGS[0]) {
    const unlocked = new Set(program.unlockedLessonSlugs || []);
    P1_TRIAL_LESSON_SLUGS.forEach(s => unlocked.add(s));
    program.unlockedLessonSlugs = Array.from(unlocked);
  }
  return writeState(state);
}

// Purchase / voucher / comp grant: FULL is the ceiling tier, so this is always
// safe to set directly.
export function grantFull(slug, source) {
  const state = readState();
  const program = state.programs[slug];
  if (!program) return state;
  program.tier = 'FULL';
  program.source = source || program.source || null;
  return writeState(state);
}

// Link the member's main CG (V/N book-code precondition). The member TYPES a CG
// name; we validate it case-insensitively against the (mock) CG directory and,
// on a match, set it as their main CG. Mirrors the real POST /courses/link-cg
// (which validates against the live CG table and sets/creates the membership).
export function linkCg(cgName) {
  const typed = String(cgName || '').trim();
  if (!typed) return { error: 'Enter your CG name' };
  const match = MOCK_CGS.find(cg => cg.name.toLowerCase() === typed.toLowerCase());
  if (!match) {
    return { error: "That CG name doesn't match a valid CG — check the spelling" };
  }
  setNsMockState({ cgLinked: true, linkedCgId: match.id, linkedCgName: match.name });
  return { linked: true, cgId: match.id, cgName: match.name };
}

// Mock IDR unlock cards. Stands in for the partner API until it is wired up, so
// the app can exercise every branch of the redeem flow on a device with no
// network dependency on IDR.
//
// A real card is opaque — nothing on it says which program it unlocks, and the
// app is never told before the server answers. The mock therefore keeps the
// mapping in a table here rather than parsing meaning out of the code, which is
// what the real lane does too (program comes from IDR's contentKey).
//
// Demo cards (alphabet-legal: no I, L, O or U; last character a letter):
//   IDRU-NS1A-FRSH-4DEF-CARD  valid    → next-steps-p1
//   IDRU-NS2A-FRSH-4DEF-CARD  valid    → next-steps-p2
//   IDRU-NS1A-SPNT-4DEF-CARD  used_up  (spent by someone else)
//   IDRU-NS1A-XPRD-4DEF-CARD  expired
//   IDRU-NS1A-CANX-4DEF-CARD  cancelled
// Any other well-formed IDRU code is not_found.
const MOCK_IDR_CARDS = {
  'IDRU-NS1A-FRSH-4DEF-CARD': { outcome: 'valid', programSlug: PROGRAM_SLUGS[0] },
  'IDRU-NS2A-FRSH-4DEF-CARD': { outcome: 'valid', programSlug: PROGRAM_SLUGS[1] },
  'IDRU-NS1A-SPNT-4DEF-CARD': { outcome: 'used_up', programSlug: PROGRAM_SLUGS[0] },
  'IDRU-NS1A-XPRD-4DEF-CARD': { outcome: 'expired', programSlug: PROGRAM_SLUGS[0] },
  'IDRU-NS1A-CANX-4DEF-CARD': { outcome: 'cancelled', programSlug: PROGRAM_SLUGS[0] },
};

// The mock's copy of what the API would return for a failed redemption. Keys
// match REDEEM_MESSAGES in qr-handler so the mock and the real lane show the
// member identical words.
const IDR_OUTCOME_ERRORS = {
  not_found: 'UNKNOWN_CODE',
  used_up: 'CODE_ALREADY_USED',
  expired: 'CODE_EXPIRED',
  cancelled: 'CODE_CANCELLED',
};

export function getMockIdrCards() {
  return MOCK_IDR_CARDS;
}

// Unlock by code. Two lanes:
//
//   IDRU-…  → IDR unlock card. Check → grant FULL → burn, in that order. CG is
//     NOT a precondition: the card is spent and the grant is written even for a
//     V/N with no CG, and Next Steps hard-blocks them on the way in until they
//     confirm one. Re-redeeming a card this member already burned replays the
//     original result instead of failing.
//   BOOK-/CLASS-  → the older hand-rolled demo codes, kept so the mock console
//     and existing specs keep working while IDR is not live.
export function redeemBookCode(rawCode) {
  const idrCode = normalizeIdrCode(rawCode);
  if (idrCode) return redeemIdrCard(idrCode);

  if (isCollectionPass(rawCode)) {
    return { error: redeemMessageForCode('COLLECTION_PASS'), errorCode: 'COLLECTION_PASS' };
  }

  const code = String(rawCode || '')
    .trim()
    .toUpperCase();
  if (!code) return { error: 'Enter a code.' };

  if (code.startsWith('BOOK')) {
    const slug = code.includes('P2') ? PROGRAM_SLUGS[1] : PROGRAM_SLUGS[0];
    grantFull(slug, 'VOUCHER');
    return { unlockedProgramSlug: slug, tier: 'FULL' };
  }

  if (code.startsWith('CLASS')) {
    const slug = code.includes('P2') ? PROGRAM_SLUGS[1] : PROGRAM_SLUGS[0];
    grantTrial(slug);
    return { unlockedProgramSlug: slug, tier: 'TRIAL' };
  }

  return { error: 'Invalid code. Try IDRU-NS1A-FRSH-4DEF-CARD, BOOK-P1-… or CLASS-P1.' };
}

function redeemIdrCard(code) {
  const state = readState();

  // Replay: this member already burned this card. IDR would return the original
  // redemption with replay:true; we return the original grant. Never an error,
  // and never a second burn.
  const alreadyBurned = state.burnedCodes[code];
  if (alreadyBurned) {
    return { unlockedProgramSlug: alreadyBurned, tier: 'FULL', replay: true };
  }

  const card = MOCK_IDR_CARDS[code];
  const outcome = card ? card.outcome : 'not_found';
  if (outcome !== 'valid') {
    const errorCode = IDR_OUTCOME_ERRORS[outcome];
    return { error: redeemMessageForCode(errorCode), errorCode };
  }

  // Grant first, burn second — IDR's documented order. If the grant threw, the
  // card would still be good and the member could try again.
  grantFull(card.programSlug, 'BOOK_CODE');
  const after = readState();
  after.burnedCodes[code] = card.programSlug;
  writeState(after);

  return { unlockedProgramSlug: card.programSlug, tier: 'FULL', replay: false };
}

// "1 lesson completed → promoted to I (Established)" — only fires once Part 1
// has at least TRIAL access (there's something to complete).
export function completePart1Lesson() {
  const state = readState();
  const p1 = state.programs[PROGRAM_SLUGS[0]];
  if (p1 && (p1.tier === 'TRIAL' || p1.tier === 'FULL')) {
    state.roleSort = Math.max(state.roleSort, 4);
  }
  return writeState(state);
}

// ── Access builder ───────────────────────────────────────────────────────────
// A granted tier is permanent (no expiry/verification in the confirmed model).
function effectiveTier(program) {
  return program.tier;
}

function buildLessonsForProgram(slug, program, tier) {
  const unlocked = new Set(program.unlockedLessonSlugs || []);
  if (slug === PROGRAM_SLUGS[0]) {
    return P1_LESSON_SLUGS.map((lessonSlug, index) => {
      // Lesson 1 is free IN FULL — every page of it, at every tier. A lesson is
      // now readable or locked and never part-way, so there is no `previewOnly`
      // and no truncated-sample treatment for the mock to reproduce.
      if (index === 0) return { slug: lessonSlug, locked: false, isTrial: false };
      const isTrialLesson = P1_TRIAL_LESSON_SLUGS.includes(lessonSlug);
      const locked = tier === 'FULL' ? false : isTrialLesson ? !unlocked.has(lessonSlug) : true;
      return { slug: lessonSlug, locked, isTrial: isTrialLesson };
    });
  }
  return P2_LESSON_SLUGS.map(lessonSlug => ({
    slug: lessonSlug,
    locked: tier !== 'FULL',
    isTrial: false,
  }));
}

// SGD $10 — a placeholder for what the server's product catalog decides. Exported
// so the mocked purchase can quote the same figure the mocked summary advertises;
// two constants would let the harness show one price and "charge" another, which
// is exactly the bug the real flow is shaped to avoid.
const DEFAULT_PRICE_CENTS = 1000;

export function mockPriceCents() {
  return DEFAULT_PRICE_CENTS;
}

function computeProgramAccess(slug, program, roleSort) {
  const tier = effectiveTier(program);
  const isP1 = slug === PROGRAM_SLUGS[0];
  const hasActiveEntitlement = tier !== 'PREVIEW';
  // Entitlements are PER-PROGRAM. Visibility is status-gated (Part 1 at G=3,
  // Part 2 at I=4 — reached by completing a Part-1 lesson) OR granted by an
  // active entitlement for THAT program (so a V/N holding a book code sees it).
  // FULL for either part comes only from that part's own entitlement (its book
  // code or an in-app purchase of that part) — owning Part 1 does NOT grant
  // Part 2 FULL. Confirmed 2026-07-22.
  const visible = isP1
    ? roleSort >= 3 || hasActiveEntitlement
    : roleSort >= 4 || hasActiveEntitlement;
  // Mirrors canPurchaseProgram: everyone on the CG ladder BELOW Regular — V, N, G,
  // I, GI — and never a Regular, who holds FULL automatically and would be sold
  // what they already have. Widened from G+ on 2026-08-30; Part 1 additionally
  // needs the program visible, Part 2 needs Part 1 owned.
  const belowRegular = roleSort >= 1 && roleSort < 5;
  const canPurchase = isP1
    ? visible && belowRegular && tier !== 'FULL'
    : belowRegular && roleSort >= 4 && tier !== 'FULL';
  return {
    slug,
    visible,
    tier,
    canPurchase,
    priceCents: DEFAULT_PRICE_CENTS,
    // Highlights, notes and the hub are on at EVERY tier — a free-in-full Lesson 1
    // that cannot be marked up is not the product being sampled. The tier already
    // decides what can be read, and that is all these act on. `prefillBlanks` is
    // the one that stays gated, and on ROLE not tier: the answer key is a Regular's.
    features: {
      blanks: true,
      highlights: true,
      notes: true,
      noteHub: true,
      prefillBlanks: roleSort >= REGULAR_PASTORAL_SORT, // R and above — see PASTORAL_OPTIONS.
    },
    lessons: buildLessonsForProgram(slug, program, tier),
  };
}

function buildAccessFor(state) {
  const p1 = state.programs[PROGRAM_SLUGS[0]];
  const p2 = state.programs[PROGRAM_SLUGS[1]];
  return {
    isLeaderGuideUser: state.roleSort >= 11,
    programs: [
      computeProgramAccess(PROGRAM_SLUGS[0], p1, state.roleSort),
      computeProgramAccess(PROGRAM_SLUGS[1], p2, state.roleSort),
    ],
  };
}

// Full synthetic access object for both programmes, from current mock state.
export function buildAccess() {
  return buildAccessFor(readState());
}

// Pure preview — same shape as buildAccess() but for a hypothetical roleSort,
// WITHOUT mutating persisted state. Powers the per-status preview matrix.
export function previewForRoleSort(roleSort) {
  return buildAccessFor({ ...readState(), roleSort });
}

// ── Feature flag seeding ─────────────────────────────────────────────────────
// Forces the flags this feature needs on. Delegates to the composable's
// seedDevFlags so BOTH the live in-memory Map (which route guards read after
// App.vue has loaded flags on boot) AND the localStorage cache are updated —
// writing only the cache is not enough once `isLoaded` is true.
export function seedFeatureFlags() {
  const data = {
    courses: true,
    vouchers: true,
    'mobile-payments': true,
    scanner: true,
    // Notes are a native feature; this test-only flag gives them back on web so a
    // PM clicking through in dev, and the courses Playwright specs, see the notes
    // surface (encryption spec, decision 7). Dev-only code — never in production.
    'courses-notes-web': true,
  };
  seedDevFlags(data);
  return data;
}
